import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';

import { COLOR_PROFILE_FIELD } from 'components/ImageViewer/constants';
import {
  COLOR_PROFILE_OPTIONS,
  resolveColorProfile,
} from 'components/ImageViewer/colorProfiles/colorProfiles';
import {
  currentUserPreferencesSelector,
  isStaffSelector,
  updatePreferences,
} from 'store/slices/userDetailsSlice';

// Staff-only config wheel that lets reviewers switch the slide's color
// profile (Default / Option A = GT450 / Option B = H&E contrast). Hidden for non-staff, and
// doing nothing until a non-Default profile is actually chosen.
// `tooltipPlacement` / `menuSide` let the floating annotation rail open them
// towards the slide; `className` styles the trigger to match the rail.
export const ColorProfileMenu = ({
  className,
  tooltipPlacement = 'bottom',
  menuSide,
}) => {
  const dispatch = useDispatch();
  const isStaff = useSelector(isStaffSelector);
  const preferences = useSelector(currentUserPreferencesSelector);
  const [anchorEl, setAnchorEl] = useState(null);

  if (!isStaff) {
    return null;
  }

  const selectedProfile = resolveColorProfile(preferences[COLOR_PROFILE_FIELD]);
  const isOpen = Boolean(anchorEl);

  const openMenu = (event) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const handleSelect = (value) => {
    dispatch(updatePreferences({ [COLOR_PROFILE_FIELD]: value }));
    closeMenu();
  };

  return (
    <>
      <Tooltip title="Viewer Presets" placement={tooltipPlacement}>
        <IconButton
          className={className}
          size="small"
          aria-label="Viewer Presets"
          aria-haspopup="true"
          aria-expanded={isOpen}
          onClick={openMenu}
        >
          <TuneIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        {...(menuSide && {
          anchorOrigin: { vertical: 'top', horizontal: menuSide },
          transformOrigin: {
            vertical: 'top',
            horizontal: menuSide === 'left' ? 'right' : 'left',
          },
          // Above the rail (z-index 2000), which shares the viewer element.
          sx: { zIndex: 2100 },
        })}
      >
        {COLOR_PROFILE_OPTIONS.map(({ value, label }) => (
          <MenuItem
            key={value}
            selected={value === selectedProfile}
            onClick={() => handleSelect(value)}
          >
            {label}
            {value === selectedProfile && (
              <CheckIcon fontSize="small" sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
